import StoreDeviceType from '../../redux/models/DeviceType'
import { getDeviceSocketUuid, isPlatformChatDeviceId } from './resolveDeviceChatIds'

/** Shared profile image for all GPS tracker / beacon chat messages (legacy parity). */
export const GPS_TRACKER_CHAT_AVATAR_SRC = '/images/gps-tracker-chat-avatar.png'

/** Legacy tracker socket ids are numeric (`3000001004`, etc.). */
export function isNumericTrackerSocketUuid (id) {
  return id != null && /^\d+$/.test(String(id))
}

/**
 * Find a device row in the Redux store by socket uuid (`uuid_device` / tracker `uuid`).
 * @param {Record<string, object>} devicesById
 * @param {string | null | undefined} socketUuid
 * @returns {object | null}
 */
export function findDeviceBySocketUuid (devicesById, socketUuid) {
  if (!socketUuid) return null
  const normalized = String(socketUuid)
  if (normalized === 'operator' || normalized === 'system') return null

  const direct = devicesById?.[normalized]
  if (direct && getDeviceSocketUuid(direct) === normalized) {
    return direct
  }

  return Object.values(devicesById || {}).find(
    device => getDeviceSocketUuid(device) === normalized
  ) || null
}

/**
 * @param {object | null | undefined} device
 * @returns {boolean}
 */
export function isGpsTrackerDevice (device) {
  if (!device) return false

  const typeName = device.type?.name ?? device.typeName
  const typeId = Number(device.typeId ?? device.type?.id)
  if (typeName === StoreDeviceType.DEVICE_TYPE_NAMES.TRACKER) return true
  if (typeId === StoreDeviceType.DEVICE_TYPE_IDS.TRACKER) return true

  // Trackers use `uuid`; phones use `uuidDevice`.
  if (device.uuid && !device.uuidDevice) return true

  return false
}

/**
 * True when the row should render as an inbound GPS tracker message (name + tracker avatar),
 * not as operator/system staff — including automation mis-tagged with senderDeviceId=operator.
 *
 * @param {object} message
 * @param {Record<string, object>} devicesById
 * @param {string | undefined} profileId
 * @returns {boolean}
 */
export function shouldPresentAsGpsTrackerInbound (message, devicesById, profileId) {
  const senderId = message.senderDeviceId != null ? String(message.senderDeviceId) : ''
  const receiverId = message.receiverDeviceId != null ? String(message.receiverDeviceId) : ''
  const senderLower = senderId.toLowerCase()

  const tracker = resolveGpsTrackerConversationDevice(message, devicesById)
  const trackerUuid = tracker ? getDeviceSocketUuid(tracker) : null

  if (trackerUuid) {
    if (senderId === trackerUuid) return true
    if (receiverId === trackerUuid) {
      if (senderLower === 'system') return true
      if (senderLower === 'operator' && message.senderUserId !== profileId) return true
    }
    return false
  }

  if (isNumericTrackerSocketUuid(senderId)) return true

  if (
    isNumericTrackerSocketUuid(receiverId) &&
    (senderLower === 'system' || (senderLower === 'operator' && message.senderUserId !== profileId))
  ) {
    return true
  }

  return false
}

/**
 * Chat bubble fields for inbound GPS tracker / zone-warning rows.
 * @param {object} message
 * @param {Record<string, object>} devicesById
 * @param {string | undefined} profileId
 * @returns {{ senderName: string, senderAvatar: string, isOwn: false, isSentByADevice: true, senderDeviceId: string | null } | null}
 */
export function resolveGpsTrackerChatPresentation (message, devicesById, profileId) {
  if (!shouldPresentAsGpsTrackerInbound(message, devicesById, profileId)) {
    return null
  }

  const tracker = resolveGpsTrackerConversationDevice(message, devicesById)
  const trackerUuid = tracker
    ? getDeviceSocketUuid(tracker)
    : (isNumericTrackerSocketUuid(message.senderDeviceId)
        ? String(message.senderDeviceId)
        : isNumericTrackerSocketUuid(message.receiverDeviceId)
          ? String(message.receiverDeviceId)
          : null)

  const senderName =
    tracker?.name ||
    message.senderDevice?.name ||
    message.receiverDevice?.name ||
    ''

  return {
    senderName,
    senderAvatar: GPS_TRACKER_CHAT_AVATAR_SRC,
    isOwn: false,
    isSentByADevice: true,
    senderDeviceId: trackerUuid
  }
}

/**
 * Resolve the GPS tracker device associated with an inbound tracker / zone-warning message.
 * @param {object} message
 * @param {Record<string, object>} devicesById
 * @returns {object | null}
 */
export function resolveGpsTrackerConversationDevice (message, devicesById) {
  if (!message) return null

  const candidateIds = [message.senderDeviceId, message.receiverDeviceId].filter(Boolean)

  for (const id of candidateIds) {
    const populated =
      message.senderDevice && getDeviceSocketUuid(message.senderDevice) === String(id)
        ? message.senderDevice
        : message.receiverDevice && getDeviceSocketUuid(message.receiverDevice) === String(id)
          ? message.receiverDevice
          : null

    if (isGpsTrackerDevice(populated)) return populated

    const fromStore = findDeviceBySocketUuid(devicesById, id)
    if (isGpsTrackerDevice(fromStore)) return fromStore
  }

  if (isGpsTrackerDevice(message.senderDevice)) return message.senderDevice
  if (isGpsTrackerDevice(message.receiverDevice)) return message.receiverDevice

  return null
}

/**
 * Device id to open a 1:1 chat thread from an inbox row.
 * Prefers the REST store id (same as the map marker shortcut) so Chat can hydrate
 * GET /tracker/:id / GET /device/:id. Operator/system senders (zone warnings,
 * tracker automation) use the target device, not the "operator" label.
 *
 * @param {object | null | undefined} message
 * @param {Record<string, object>} [devicesById]
 * @returns {string | null}
 */
export function resolveInboxThreadDeviceId (message, devicesById) {
  if (!message) return null

  const tracker = resolveGpsTrackerConversationDevice(message, devicesById || {})
  if (tracker?.id) return String(tracker.id)
  const trackerUuid = tracker ? getDeviceSocketUuid(tracker) : null
  if (trackerUuid) return trackerUuid

  const senderId = message.senderDeviceId != null ? String(message.senderDeviceId) : ''
  const receiverId = message.receiverDeviceId != null ? String(message.receiverDeviceId) : ''

  if (!senderId || isPlatformChatDeviceId(senderId)) {
    if (message.receiverDevice?.id) return String(message.receiverDevice.id)
    if (receiverId && !isPlatformChatDeviceId(receiverId)) return receiverId
    return null
  }

  if (message.senderDevice?.id) return String(message.senderDevice.id)
  return senderId
}
